import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActionActivityService } from '../../services/action-activity/action-activity.service';

/** Ignore work that finishes quickly: a bar nobody can read is just a flicker. */
const APPEAR_DELAY_MS = 120;

/** Once it is on screen, keep it there long enough to be seen. */
const MINIMUM_VISIBLE_MS = 320;

/**
 * A thin progress bar across the top of the application.
 *
 * It reports any work the application is waiting on — a request, a route
 * change — without blocking the screen behind it. Mounted once at the root;
 * nothing else has to opt in.
 */
@Component({
  selector: 'lib-global-progress-bar',
  standalone: true,
  imports: [],
  templateUrl: './global-progress-bar.html',
  styleUrl: './global-progress-bar.css',
})
export class GlobalProgressBarComponent {
  private readonly activity = inject(ActionActivityService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly shown = signal(false);
  private shownAt = 0;
  private appearTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  readonly visible = computed(() => this.shown());

  constructor() {
    effect(() => {
      const busy = this.activity.isActive();
      if (busy) {
        this.scheduleAppear();
      } else {
        this.scheduleHide();
      }
    });

    this.destroyRef.onDestroy(() => {
      this.clearAppearTimer();
      this.clearHideTimer();
    });
  }

  private scheduleAppear(): void {
    this.clearHideTimer();
    if (this.shown() || this.appearTimer !== null) {
      return;
    }

    this.appearTimer = setTimeout(() => {
      this.appearTimer = null;
      this.shownAt = Date.now();
      this.shown.set(true);
    }, APPEAR_DELAY_MS);
  }

  private scheduleHide(): void {
    this.clearAppearTimer();
    if (!this.shown() || this.hideTimer !== null) {
      return;
    }

    const shownFor = Date.now() - this.shownAt;
    const remaining = Math.max(0, MINIMUM_VISIBLE_MS - shownFor);

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.shown.set(false);
    }, remaining);
  }

  private clearAppearTimer(): void {
    if (this.appearTimer !== null) {
      clearTimeout(this.appearTimer);
      this.appearTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
